import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { Search, Printer } from "lucide-react";
import { fetcher, asList, imgUrl } from "@/lib/api";
import { ItemCard } from "@/components/ItemCard";
import { MaskTitle } from "@/components/MaskTitle";
import { Eyebrow } from "@/components/Reveal";
import { FilterRow, STYLES, itemFinishes } from "@/components/FilterRow";
import { PackageBuilder } from "@/components/PackageBuilder";

export default function CategoryPage() {
  const { slug } = useParams();
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const supplier = params.get("supplier") || "";
  const style = params.get("style") || "";
  const finish = params.get("finish") || "";
  const { data: catData } = useSWR("/categories", fetcher);
  const { data: itemData, isLoading } = useSWR(`/items?category=${slug}`, fetcher);
  const cats = asList(catData);
  const cat = cats.find((c) => c.slug === slug);
  const term = q.trim().toLowerCase();
  const all = asList(itemData);
  const suppliers = [...new Set(all.map((i) => i.supplier).filter(Boolean))].sort();
  const styleVals = [...new Set(all.map((i) => i.style).filter(Boolean))];
  const styles = [...STYLES.filter((s) => styleVals.includes(s)), ...styleVals.filter((s) => !STYLES.includes(s)).sort()];
  const finishOpts = Object.entries(all.flatMap(itemFinishes).reduce((m, f) => ({ ...m, [f]: (m[f] || 0) + 1 }), {}))
    .sort((a, b) => b[1] - a[1]).map(([f]) => f);
  const pass = {
    supplier: (i) => !supplier || i.supplier === supplier,
    style: (i) => !style || i.style === style,
    finish: (i) => !finish || itemFinishes(i).includes(finish),
  };
  const except = (k) => all.filter((i) => Object.entries(pass).every(([n, f]) => n === k || f(i)));
  const items = all.filter((it) => pass.supplier(it) && pass.style(it) && pass.finish(it) && (!term ||
    [it.name, it.supplier, it.code, it.description].some((v) => (v || "").toLowerCase().includes(term))));
  const compact = slug === "board-products";
  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };
  const printQs = new URLSearchParams({ cat: slug, ...(supplier && { supplier }), ...(style && { style }), ...(finish && { finish }) }).toString();

  return (
    <main className="pt-20">
      <section className="relative h-[52vh] min-h-[380px] overflow-hidden border-b border-[#D4AF37]/15">
        {cat?.cover_image && <img src={imgUrl(cat.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 scale-105" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D11] via-[#0D0D11]/50 to-[#0D0D11]/30" />
        <div className="relative h-full mx-auto max-w-[1440px] px-5 sm:px-10 flex flex-col justify-end pb-12">
          <Eyebrow><Link to="/" className="hover:text-[#F5F3EF]">Atlas</Link> / Collection · {items.length} items</Eyebrow>
          <MaskTitle key={slug} text={cat?.name || " "} className="mt-5 font-display font-light text-6xl sm:text-7xl lg:text-8xl text-[#F5F3EF]" />
          {cat?.description && <p className="mt-4 max-w-lg text-[#A09E97]">{cat.description}</p>}
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 sm:px-10 py-10 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {cats.map((c) => (
            <Link key={c.id} to={`/c/${c.slug}`} data-testid={`cat-tab-${c.slug}`} onClick={() => setQ("")}
              className={`shrink-0 rounded-full px-4 py-2 text-sm border transition-colors duration-300 ${c.slug === slug ? "bg-[#D4AF37] text-[#0D0D11] border-[#D4AF37]" : "border-[#D4AF37]/25 text-[#A09E97] hover:text-[#F5F3EF]"}`}>
              {c.name}
            </Link>
          ))}
        </div>
        <div className="flex gap-3">
          <label className="relative flex-1 sm:w-64">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A09E97]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, code, supplier" data-testid="category-search-input"
              className="w-full h-11 rounded-full bg-[#13141C] border border-[#D4AF37]/25 pl-11 pr-4 text-sm text-[#F5F3EF] placeholder:text-[#A09E97] focus:outline-none focus:border-[#D4AF37]" />
          </label>
          <Link to={`/print?${printQs}`} data-testid="category-print-btn" aria-label="Print this category"
            className="h-11 shrink-0 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 text-[#E5C158] hover:bg-[#D4AF37] hover:text-[#0D0D11] transition-colors duration-300">
            <Printer className="w-4 h-4" /><span className="hidden md:inline text-sm">Print</span>
          </Link>
        </div>
      </section>

      {slug === "appliances" && <PackageBuilder items={all} />}
      {(slug === "board-products" || slug === "stone-options") && (
        <div className="mx-auto max-w-[1440px] px-5 sm:px-10 pb-8">
          <Link to="/pairing" data-testid="category-pairing-link"
            className="group flex items-center justify-between gap-4 border border-[#D4AF37]/25 bg-[#13141C] px-5 sm:px-8 py-5 hover:border-[#D4AF37]/60 transition-colors">
            <span><span className="block font-mono-j text-[11px] uppercase tracking-[0.25em] text-[#C5A059]">Pairing studio</span>
              <span className="block mt-1 font-display text-2xl sm:text-3xl text-[#F5F3EF]">See a {slug === "board-products" ? "board with a benchtop stone" : "stone with a cabinet board"}</span></span>
            <span className="shrink-0 h-11 px-5 inline-flex items-center rounded-full bg-[#D4AF37] text-sm font-medium text-[#0D0D11] group-hover:bg-[#E5C158] transition-colors">Open</span>
          </Link>
        </div>
      )}

      <div className="-mt-4 pb-10 space-y-3">
        {suppliers.length > 1 && (
          <FilterRow label="Supplier" id="supplier" options={suppliers} value={supplier} pool={except("supplier")} field="supplier" onChange={(v) => setFilter("supplier", v)} />
        )}
        {styles.length > 1 && (
          <FilterRow label={slug === "board-products" ? "Style" : "Type"} id="style" options={styles} value={style} pool={except("style")} field="style" onChange={(v) => setFilter("style", v)} />
        )}
        {finishOpts.length > 1 && (
          <FilterRow label="Finish" id="finish" options={finishOpts} value={finish} pool={except("finish")} match={(i, o) => itemFinishes(i).includes(o)} onChange={(v) => setFilter("finish", v)} />
        )}
      </div>

      <section className={`mx-auto max-w-[1440px] px-5 sm:px-10 grid ${compact ? "grid-cols-2 gap-x-3 gap-y-10" : "grid-cols-1 gap-y-16"} sm:grid-cols-2 lg:grid-cols-3 sm:gap-x-8 sm:gap-y-16`} data-testid="category-items-grid">
        {items.map((it, i) => <ItemCard key={it.id} item={it} i={i} compact={compact} />)}
        {!isLoading && items.length === 0 && (
          <p className="col-span-full py-24 text-center font-display italic text-3xl text-[#A09E97]" data-testid="category-empty">Nothing here yet.</p>
        )}
      </section>
    </main>
  );
}
